/**
 * Layout reconstruction. Recognition output is kept as positioned tokens for as
 * long as possible; this module rebuilds reading order and table structure from
 * geometry alone. No hard-coded coordinates and no sample-specific text.
 */

export interface BoundingBox {
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface OcrToken {
  text: string;
  /** 0-100 where the engine reports it, otherwise null. */
  confidence: number | null;
  boundingBox: BoundingBox;
  page: number;
  engine: string;
}

export interface LayoutLine {
  tokens: OcrToken[];
  text: string;
  top: number;
  bottom: number;
  centerY: number;
  height: number;
  confidence: number | null;
}

export interface LayoutTable {
  /** Column x-bands, left to right. */
  bands: { start: number; end: number }[];
  /** Every row, already in left-to-right column order. Empty cells preserved. */
  rows: string[][];
}

export type LayoutBlock =
  | { kind: 'lines'; lines: LayoutLine[] }
  | { kind: 'table'; table: LayoutTable; lines: LayoutLine[] };

export interface LayoutResult {
  blocks: LayoutBlock[];
  lines: LayoutLine[];
  tables: LayoutTable[];
  text: string;
  medianTokenHeight: number;
}

function median(values: number[]): number {
  if (!values.length) return 0;
  const sorted = values.slice().sort((a, b) => a - b);
  const mid = sorted.length >> 1;
  return sorted.length % 2 ? sorted[mid] : (sorted[mid - 1] + sorted[mid]) / 2;
}

function average(values: (number | null)[]): number | null {
  const usable = values.filter((v): v is number => typeof v === 'number' && Number.isFinite(v));
  if (!usable.length) return null;
  return usable.reduce((a, b) => a + b, 0) / usable.length;
}

export function medianTokenHeight(tokens: OcrToken[]): number {
  return median(tokens.map((t) => t.boundingBox.height).filter((h) => h > 0));
}

/**
 * Groups tokens into lines using vertical centres with a tolerance adapted to
 * the median text height.
 */
export function groupTokensIntoLines(tokens: OcrToken[], toleranceRatio = 0.6): LayoutLine[] {
  const usable = tokens.filter((t) => t.text && t.text.trim() && t.boundingBox.height > 0);
  if (!usable.length) return [];
  const tol = Math.max(2, medianTokenHeight(usable) * toleranceRatio);

  const sorted = usable
    .slice()
    .sort((a, b) => a.boundingBox.y + a.boundingBox.height / 2 - (b.boundingBox.y + b.boundingBox.height / 2));

  const buckets: OcrToken[][] = [];
  let current: OcrToken[] = [];
  let currentCenter = 0;

  sorted.forEach((token) => {
    const center = token.boundingBox.y + token.boundingBox.height / 2;
    if (!current.length) {
      current = [token];
      currentCenter = center;
      return;
    }
    if (Math.abs(center - currentCenter) <= tol) {
      current.push(token);
      currentCenter = current.reduce((s, t) => s + t.boundingBox.y + t.boundingBox.height / 2, 0) / current.length;
      return;
    }
    buckets.push(current);
    current = [token];
    currentCenter = center;
  });
  if (current.length) buckets.push(current);

  return buckets
    .map((bucket) => {
      const ordered = bucket.slice().sort((a, b) => a.boundingBox.x - b.boundingBox.x);
      const top = Math.min(...ordered.map((t) => t.boundingBox.y));
      const bottom = Math.max(...ordered.map((t) => t.boundingBox.y + t.boundingBox.height));
      return {
        tokens: ordered,
        text: ordered.map((t) => t.text).join(' ').replace(/\s{2,}/g, ' ').trim(),
        top,
        bottom,
        centerY: (top + bottom) / 2,
        height: bottom - top,
        confidence: average(ordered.map((t) => t.confidence)),
      };
    })
    .sort((a, b) => a.centerY - b.centerY);
}

/** Splits lines into vertical blocks wherever the gap is clearly larger than usual. */
export function splitIntoBlocks(lines: LayoutLine[], gapFactor = 2.1): LayoutLine[][] {
  if (lines.length < 2) return lines.length ? [lines] : [];
  const gaps: number[] = [];
  for (let i = 1; i < lines.length; i += 1) gaps.push(Math.max(0, lines[i].top - lines[i - 1].bottom));
  const typicalGap = median(gaps.filter((g) => g >= 0));
  const lineHeight = median(lines.map((l) => l.height));
  const threshold = Math.max(typicalGap * gapFactor, lineHeight * 1.2, 4);

  const blocks: LayoutLine[][] = [];
  let block: LayoutLine[] = [lines[0]];
  for (let i = 1; i < lines.length; i += 1) {
    if (lines[i].top - lines[i - 1].bottom > threshold) {
      blocks.push(block);
      block = [lines[i]];
    } else {
      block.push(lines[i]);
    }
  }
  blocks.push(block);
  return blocks;
}

/**
 * Finds column bands by projecting every token's horizontal extent and looking
 * for consistently empty vertical corridors.
 */
export function findColumnBands(lines: LayoutLine[], minGapRatio = 1.6): { start: number; end: number }[] {
  const tokens = lines.flatMap((l) => l.tokens);
  if (tokens.length < 4) return [];
  const left = Math.min(...tokens.map((t) => t.boundingBox.x));
  const right = Math.max(...tokens.map((t) => t.boundingBox.x + t.boundingBox.width));
  const span = right - left;
  if (span <= 0) return [];

  const tokenHeight = medianTokenHeight(tokens);
  const minGap = Math.max(tokenHeight * minGapRatio, span * 0.012, 3);

  const intervals = tokens
    .map((t) => ({ a: t.boundingBox.x, b: t.boundingBox.x + t.boundingBox.width }))
    .sort((p, q) => p.a - q.a);

  const bands: { start: number; end: number }[] = [];
  let start = intervals[0].a;
  let end = intervals[0].b;
  intervals.slice(1).forEach((iv) => {
    if (iv.a - end > minGap) {
      bands.push({ start, end });
      start = iv.a;
      end = iv.b;
    } else if (iv.b > end) {
      end = iv.b;
    }
  });
  bands.push({ start, end });
  return bands;
}

function bandIndexFor(token: OcrToken, bands: { start: number; end: number }[]): number {
  const center = token.boundingBox.x + token.boundingBox.width / 2;
  let best = -1;
  let bestDistance = Infinity;
  bands.forEach((band, index) => {
    if (center >= band.start && center <= band.end) {
      best = index;
      bestDistance = 0;
      return;
    }
    const distance = center < band.start ? band.start - center : center - band.end;
    if (distance < bestDistance) {
      bestDistance = distance;
      best = index;
    }
  });
  return best;
}

export interface TableDetection {
  isTable: boolean;
  bands: { start: number; end: number }[];
  multiBandLineCount: number;
}

/**
 * A block looks tabular when several of its lines place content in two or more
 * of the same vertical column bands.
 */
export function detectTable(lines: LayoutLine[]): TableDetection {
  const bands = findColumnBands(lines);
  if (bands.length < 2 || lines.length < 3) {
    return { isTable: false, bands, multiBandLineCount: 0 };
  }
  let multiBand = 0;
  lines.forEach((line) => {
    const used = new Set(line.tokens.map((t) => bandIndexFor(t, bands)));
    if (used.size >= 2) multiBand += 1;
  });
  const ratio = multiBand / lines.length;
  const isTable = multiBand >= 3 && ratio >= 0.6;
  return { isTable, bands, multiBandLineCount: multiBand };
}

/**
 * Builds rows from a tabular block. Lines whose first column is empty are
 * treated as wrapped continuations of the row above.
 */
export function buildTable(lines: LayoutLine[], bands: { start: number; end: number }[]): LayoutTable {
  const rows: string[][] = [];
  lines.forEach((line) => {
    const cells: string[][] = bands.map(() => []);
    line.tokens.forEach((token) => {
      const idx = bandIndexFor(token, bands);
      if (idx >= 0) cells[idx].push(token.text);
    });
    const flat = cells.map((parts) => parts.join(' ').replace(/\s{2,}/g, ' ').trim());
    if (flat.every((c) => !c)) return;

    const isContinuation = rows.length > 0 && !flat[0];
    if (isContinuation) {
      const previous = rows[rows.length - 1];
      flat.forEach((cell, i) => {
        if (!cell) return;
        previous[i] = previous[i] ? `${previous[i]} ${cell}` : cell;
      });
      return;
    }
    rows.push(flat);
  });
  return { bands, rows };
}

export function tableToText(table: LayoutTable): string {
  return table.rows.map((row) => row.join(' | ').replace(/\s+\|/g, ' |').trimEnd()).join('\n');
}

export interface ReconstructOptions {
  /** Skip table detection (used when the engine only reports whole lines). */
  detectTables?: boolean;
}

export function reconstructLayout(tokens: OcrToken[], options: ReconstructOptions = {}): LayoutResult {
  const { detectTables = true } = options;
  const lines = groupTokensIntoLines(tokens);
  if (!lines.length) {
    return { blocks: [], lines: [], tables: [], text: '', medianTokenHeight: 0 };
  }

  const blocks: LayoutBlock[] = [];
  const tables: LayoutTable[] = [];
  const chunks = splitIntoBlocks(lines);

  chunks.forEach((chunk) => {
    if (detectTables) {
      const detection = detectTable(chunk);
      if (detection.isTable) {
        const table = buildTable(chunk, detection.bands);
        tables.push(table);
        blocks.push({ kind: 'table', table, lines: chunk });
        return;
      }
    }
    blocks.push({ kind: 'lines', lines: chunk });
  });

  const text = blocks
    .map((block) => (block.kind === 'table' ? tableToText(block.table) : block.lines.map((l) => l.text).join('\n')))
    .join('\n\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();

  return { blocks, lines, tables, text, medianTokenHeight: medianTokenHeight(tokens) };
}
